import React from 'react';

/**
 * Reusable Visual Empty State Component
 * Provides a modern, glassmorphic empty state card with icon, title, description,
 * and optional primary/secondary action buttons.
 */
export default function EmptyState({
  icon = '📂',
  title = 'No Data Available',
  description = 'There are currently no items to display in this view.',
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  compact = false,
  style = {}
}) {
  return (
    <div style={{ ...styles.container, ...(compact ? styles.containerCompact : {}), ...style }}>
      {/* Icon Badge */}
      <div style={{ ...styles.iconCircle, ...(compact ? styles.iconCircleCompact : {}) }}>
        {typeof icon === 'string' ? (
          <span style={{ fontSize: compact ? '24px' : '32px' }}>{icon}</span>
        ) : (
          icon
        )}
      </div>

      {/* Typography */}
      <h3 style={{ ...styles.title, ...(compact ? styles.titleCompact : {}) }}>
        {title}
      </h3>
      
      {description && (
        <p style={{ ...styles.description, ...(compact ? styles.descriptionCompact : {}) }}>
          {description}
        </p>
      )}

      {/* Action Buttons */}
      {(actionLabel || secondaryActionLabel) && (
        <div style={styles.actionsRow}>
          {actionLabel && onAction && (
            <button
              onClick={onAction}
              style={styles.primaryBtn}
              type="button"
            >
              {actionLabel}
            </button>
          )}

          {secondaryActionLabel && onSecondaryAction && (
            <button
              onClick={onSecondaryAction}
              style={styles.secondaryBtn}
              type="button"
            >
              {secondaryActionLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '48px 32px',
    textAlign: 'center',
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.75))',
    backdropFilter: 'blur(16px)',
    border: '1px solid var(--border-light, #e2e8f0)',
    borderRadius: '20px',
    boxShadow: 'var(--shadow-card, 0 4px 16px rgba(0,0,0,0.04))',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '16px 0',
    width: '100%',
    boxSizing: 'border-box'
  },
  containerCompact: {
    padding: '24px 16px',
    borderRadius: '14px',
    margin: '8px 0',
    background: 'rgba(248, 250, 252, 0.6)'
  },
  iconCircle: {
    width: '64px',
    height: '64px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(99, 102, 241, 0.16) 100%)',
    border: '1px solid rgba(79, 70, 229, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
    boxShadow: '0 8px 16px rgba(79, 70, 229, 0.06)'
  },
  iconCircleCompact: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    marginBottom: '10px'
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '17px',
    fontWeight: '700',
    color: 'var(--text-primary, #0f172a)',
    letterSpacing: '-0.01em'
  },
  titleCompact: {
    fontSize: '13.5px',
    marginBottom: '4px'
  },
  description: {
    margin: '0 0 20px 0',
    fontSize: '13.5px',
    color: 'var(--text-muted, #64748b)',
    maxWidth: '460px',
    lineHeight: '1.5'
  },
  descriptionCompact: {
    fontSize: '12px',
    maxWidth: '280px',
    marginBottom: '12px'
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  primaryBtn: {
    background: 'linear-gradient(135deg, var(--primary-color, #4f46e5) 0%, #3b82f6 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '10px 20px',
    fontSize: '13.5px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  secondaryBtn: {
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-secondary, #334155)',
    border: '1px solid var(--border-color, #cbd5e1)',
    borderRadius: '10px',
    padding: '9px 18px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.15s ease'
  }
};
